<script setup lang="ts">
  interface Props {
    editBlock: { id: number; text: string };
    isEdit: boolean;
    enableMemberOnly?: boolean; // 会員限定要素を挿入するボタンを表示するかどうか
    enableHeadings?: boolean; // 見出しH1、H2、H3の挿入を許可するかどうか。falseの場合はH3のみ許可
    uploadImageFile?: (f: File) => Promise<string>; // 画像アップロード処理をカスタムしたい場合は、この関数を渡す
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "updateText", text: string): void;
    (event: "removeBlock", id: number): void;
    (event: "sortBlock", fromIndex: number, toIndex: number): void;
  }
  const emit = defineEmits<Emits>();

  // キャンセル確認ダイアログ用
  const isCancelDialogOpened = ref(false);

  const onClickCancelYes = () => {
    // ブロック削除確認ダイアログではいがクリックされた際の処理
    emit("removeBlock", props.editBlock.id);
  };
  const onClickCancelNo = () => {
    // キャンセル確認ダイアログでいいえがクリックされた際の処理
    isCancelDialogOpened.value = false;
  };

  const onRemoveBlock = () => {
    // キャンセル確認ダイアログを表示する
    isCancelDialogOpened.value = true;
  };

  const onSortBlock = (fromIndex: number, toIndex: number) => {
    emit("sortBlock", fromIndex, toIndex);
  };

  const computedData = computed<string>({
    get: () => props.editBlock.text,
    set: value => {
      emit("updateText", value);
    },
  });
  const convertMark = (content: string) => {
    // ui, liなどは改行がないので追加する
    const ret = content.replace(/<ul><li>/g, "<ul>\n<li>").replace(/<\/li></g, "</li>\n<");
    // windowsでスクロールバーが表示されるので、iframeにはscrolling属性を追加する
    return ret.replace(/<iframe/g, '<iframe scrolling="no"');
  };
</script>

<!-- markd を sanitize すると内容が崩れるのでそのまま -->
<!-- ref: https://github.com/Fincs-dev/fincs-webfront/pull/202 -->
<!-- eslint-disable vue/no-v-html -->
<template>
  <template v-if="isEdit">
    <!-- 順番変更ボタン -->
    <v-icon icon="$mdiSortVariant" class="handle" />
    <v-icon icon="$mdiChevronUp" class="up-button cursor-pointer" @click="onSortBlock(editBlock.id, -1)" />
    <v-icon icon="$mdiChevronDown" class="down-button cursor-pointer" @click="onSortBlock(editBlock.id, +1)" />
    <!-- クローズボタン -->
    <v-icon icon="$mdiClose" class="close-button cursor-pointer" @click="onRemoveBlock()" />
    <!-- エディター表示 -->
    <OrganismsRichEditor
      v-model:value="computedData"
      :readonly="false"
      :upload-image-file="uploadImageFile"
      :enable-member-only="enableMemberOnly"
      :enable-headings="enableHeadings"
      class="markdown"
    />
  </template>
  <template v-else>
    <div class="text-left ql-editor markdown" v-html="convertMark(editBlock.text)" />
  </template>
  <MoleculesYesNoDialog
    v-model:is-open="isCancelDialogOpened"
    message="ブロックを削除します。本当によろしいですか？"
    @click-yes="onClickCancelYes"
    @click-no="onClickCancelNo"
  />
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";
  .handle {
    position: absolute;
    top: 10px;
    left: 10px;
    cursor: move;
  }
  .up-button {
    position: absolute;
    top: 10px;
    left: 40px;
  }
  .down-button {
    position: absolute;
    top: 10px;
    left: 70px;
  }
  .close-button {
    position: absolute;
    top: 10px;
    right: 10px;
  }

  .markdown {
    :deep(iframe) {
      width: 100%;
      max-width: 100%;
      aspect-ratio: 16 / 9;
      @include display_pc {
        height: auto;
      }
    }
  }
</style>
